import { makeStyles, Spinner } from "@fluentui/react-components";
import { format } from "date-fns";
import { useContext, useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { LayoutContext } from "../../../context/LayoutContext";
import { useApiCall } from "../../../hooks/useApiCall";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { EMPTY_STATE_MESSAGE } from "../../../utils/common/Constants";
import { getDateTimeForTimeZone } from "../../../utils/common/DateHelperFunctions";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import createCapacityChartPlugin from "./FlightCapacityChartPlugin";

export default function FlightDemandCapacityChart({
  runway,
  selectedButton,
  airport,
  timeRef,
  refresh,
}: any) {
  const { globalFilterDate } = useContext(LayoutContext);
  const [chartData, setChartData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState<string>("");
  const { timeZoneId } = getAirportConfig(airport);
  const exactTimeIndexRef = useRef<number>(-1);

  const getDemandCapacityUrl = `${process.env.REACT_APP_API_RUNWAY_CAPACITY_CHART?.replace(
    "{airportCode}",
    airport
  )}`;

  const payload = {
    start: format(globalFilterDate.startDate, "yyyy-MM-dd'T'HH:mm:ss"),
    end: format(globalFilterDate.endDate, "yyyy-MM-dd'T'HH:mm:ss"),
    timeReference: timeRef,
    tz: "LT",
    runway,
    interval: 60,
    timeMode: "Best",
  };

  const {
    data: demandCapacityResponse,
    error,
    isLoading: isLoadingCapacity,
  } = useApiCall<any>({
    url: getDemandCapacityUrl,
    requestType: "post",
    payload,
    dependencies: [airport, runway, timeRef, refresh, globalFilterDate],
  });

  const resourceData = demandCapacityResponse?.dateTimeSeries;
  const classes = useStyles();

  const getFormattedTime = () => {
    const now = getDateTimeForTimeZone(new Date(), timeZoneId);
    return format(now, "HH:mm");
  };

  const computeExactTimeIndex = (resourceData: any[]) => {
    if (!resourceData) return -1;
    const formattedTime = getFormattedTime();
    return resourceData.findIndex((entry: any) => {
      const entryStartTime = format(new Date(entry.datetime), "HH:mm");
      const entryEndTime = format(
        new Date(new Date(entry.datetime).getTime() + 59 * 60 * 1000),
        "HH:mm"
      );
      return formattedTime >= entryStartTime && formattedTime <= entryEndTime;
    });
  };

  // Update current time every minute
  useEffect(() => {
    const updateCurrentTime = () => setCurrentTime(getFormattedTime());
    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Update chart whenever data or time changes
  useEffect(() => {
    if (!resourceData || isLoadingCapacity) return;

    const exactTimeIndex = computeExactTimeIndex(resourceData);
    exactTimeIndexRef.current = exactTimeIndex;

    const newChartData = {
      labels: resourceData.map((entry: any, index: number) => {
        const date = format(new Date(entry.datetime), "yyyy-MM-dd");
        const startTime = format(new Date(entry.datetime), "HH:mm");
        if (index === 0) return `(${date}) ${startTime}`;
        const prevDate = format(
          new Date(resourceData[index - 1].datetime),
          "yyyy-MM-dd"
        );
        return prevDate !== date ? `(${date}) ${startTime}` : `${startTime}`;
      }),
      datasets: [
        {
          type: "bar" as const,
          label: "MVTs",
          hoverBorderColor: "#42A0F0",
          hoverBorderWidth: 4,
          backgroundColor: resourceData.map((_: any, i: number) =>
            i === exactTimeIndex
              ? "rgb(12, 102, 225)"
              : "rgba(66, 160, 240, 0.8)"
          ),
          borderColor: "rgba(66, 160, 240, 0.8)",
          data: resourceData.map((entry: any) => entry.total),
          order: 2,
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const entry = resourceData[context.dataIndex];
                const isCurrent =
                  context.dataIndex === exactTimeIndexRef.current;
                const base = [
                  `Total Flights: ${entry.total}`,
                  `Arrivals: ${entry.arrival}`,
                  `Departures: ${entry.departure}`,
                  `Capacity: ${entry.capacity}`,
                ];
                if (isCurrent) {
                  return [`Current LT Time: ${currentTime}`, ...base];
                }
                return base;
              },
            },
          },
        },
        {
          type: "line" as const,
          label: "Capacity",
          data: resourceData.map((entry: any) => entry.capacity),
          borderColor: "rgb(123, 97, 255)",
          borderWidth: 2,
          fill: false,
        },
        {
          type: "line" as const,
          label: "Arrival",
          data: resourceData.map((entry: any) => entry.arrival),
          borderColor: "#1a7431",
          borderWidth: 2,
          fill: false,
        },
        {
          type: "line" as const,
          label: "Departure",
          data: resourceData.map((entry: any) => entry.departure),
          borderColor: "#fb6107",
          borderWidth: 2,
          fill: false,
        },
      ],
      options: {
        plugins: {
          legend: { display: false, position: "bottom" },
        },
        scales: {
          x: {
            grid: { display: false },
            title: { display: true, text: "Time in LT" },
          },
          y: {
            grid: { display: true },
            title: { display: true, text: "Runway Demand" },
          },
        },
        animation: { duration: 0 },
        tooltip: { enabled: true, mode: "index", intersect: false },
      },
    };

    setChartData(newChartData);
    setIsLoading(false);
  }, [
    resourceData,
    currentTime,
    airport,
    runway,
    isLoadingCapacity,
    timeZoneId,
    globalFilterDate,
  ]);

  // 🔥 Plugin instance defined per render (not global)
  const dynamicVerticalLinePlugin = {
    id: "dynamicVerticalLinePlugin",
    afterDraw: (chart: any) => {
      const idx = exactTimeIndexRef.current;
      if (idx === -1) return;

      const xAxis = chart.scales.x;
      const xPos = xAxis?.getPixelForValue(idx);
      if (xPos === undefined) return;

      const ctx = chart?.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(xPos, chart.chartArea.top);
      ctx.lineTo(xPos, chart.chartArea.bottom);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "black";
      ctx.stroke();
      ctx.restore();

      // 🔒 Safe update: only refresh if chart still mounted
      const timeoutId = setTimeout(() => {
        if (chart.canvas && !chart._destroyed) {
          try {
            chart.update("none");
          } catch (e) {
            console.warn("Chart update skipped (chart unmounted).");
          }
        }
      }, 1000);

      // Cleanup on destroy
      chart.$verticalLineTimeout = timeoutId;
    },
    beforeDestroy: (chart: any) => {
      if (chart.$verticalLineTimeout) {
        clearTimeout(chart.$verticalLineTimeout);
      }
    },
  };

  return (
    <div data-testid="capacity-chart">
      {error && (
        <div style={{ paddingTop: "200px" }}>
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        </div>
      )}
      {isLoadingCapacity || isLoading ? (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.cardLoader}
        />
      ) : null}
      {!error && !isLoadingCapacity && !isLoading && chartData ? (
        <div
          className={classes.container}
          style={{ width: "80%", margin: "70px auto" }}
        >
          <Line
            key={`${airport}-${runway}-${currentTime}-${
              resourceData?.length || 0
            }`}
            data={chartData}
            options={chartData.options}
            plugins={[
              createCapacityChartPlugin(selectedButton),
              dynamicVerticalLinePlugin,
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

const useStyles = makeStyles({
  container: { height: "450px" },
  cardLoader: { height: "100%", paddingTop: "200px" },
});
